import React, { useEffect, useState } from 'react';
import { Store, visionService } from '../../services/database/vision';
import { FinancialAnalytics } from './FinancialAnalytics';
import { MetricCard } from './MetricCard';
import { PlanTable } from './PlanTable';
import { FinanceHeader } from './FinanceHeader';
import { SettlementView } from './SettlementView';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  PieChart,
  Users,
  Target
} from 'lucide-react';

interface FinanceListProps {
  isBlurred: boolean;
}

export const FinanceList: React.FC<FinanceListProps> = ({ isBlurred }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [showValues, setShowValues] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'details' | 'settlement'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState({ 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear() 
  });

  const loadStores = async () => {
    try {
      setIsLoading(true);
      const data = await visionService.getStores();
      setStores(data);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStores();
  }, []);

  const filteredStores = stores.filter(store => {
    const storeDate = new Date(store.created_at);
    return (
      storeDate.getMonth() + 1 === selectedDate.month &&
      storeDate.getFullYear() === selectedDate.year
    );
  });

  const calculateMetrics = () => {
    const totalRevenue = filteredStores.reduce((sum, store) => sum + (store.price || 0), 0);
    const totalCosts = filteredStores.reduce((sum, store) => {
      const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
      return sum + Object.values(costs).reduce((a, b) => a + b, 0);
    }, 0);
    const totalProfit = totalRevenue - totalCosts;
    const profitMargin = totalRevenue ? (totalProfit / totalRevenue) * 100 : 0;

    // Calcular crescimento (comparando com o mês anterior)
    const previousMonth = selectedDate.month === 1 
      ? { month: 12, year: selectedDate.year - 1 }
      : { month: selectedDate.month - 1, year: selectedDate.year };

    const previousPeriodStores = stores.filter(store => {
      const storeDate = new Date(store.created_at);
      return (
        storeDate.getMonth() + 1 === previousMonth.month &&
        storeDate.getFullYear() === previousMonth.year
      );
    });

    const previousRevenue = previousPeriodStores.reduce((sum, store) => sum + (store.price || 0), 0);
    const revenueGrowth = previousRevenue ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 : 0;

    const previousProfit = previousPeriodStores.reduce((sum, store) => {
      const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
      const totalCost = Object.values(costs).reduce((a, b) => a + b, 0);
      return sum + ((store.price || 0) - totalCost);
    }, 0);
    const profitGrowth = previousProfit ? ((totalProfit - previousProfit) / previousProfit) * 100 : 0;

    return {
      revenue: totalRevenue,
      costs: totalCosts,
      profit: totalProfit,
      profitMargin,
      revenueGrowth,
      profitGrowth,
      totalStores: filteredStores.length
    };
  };

  const metrics = calculateMetrics();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const brandingStores = filteredStores.filter(store => store.plan === 'branding');
  const premiumStores = filteredStores.filter(store => store.plan === 'premium');
  const startStores = filteredStores.filter(store => store.plan === 'start');

  const formatMonthYear = (month: number, year: number) => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const handleNavigateMonth = (direction: 'prev' | 'next') => {
    setSelectedDate(prev => {
      const newDate = new Date(prev.year, prev.month - 1);
      if (direction === 'next') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
      return {
        month: newDate.getMonth() + 1,
        year: newDate.getFullYear()
      };
    });
  };

  return (
    <div className={`min-h-screen ${isBlurred ? 'blur-md' : ''}`}>
      {/* Cabeçalho com Filtros */}
      <FinanceHeader
        showValues={showValues}
        onToggleValues={() => setShowValues(!showValues)}
        selectedDate={selectedDate}
        onNavigateMonth={(direction) => {
          setSelectedDate(prev => {
            const newDate = new Date(prev.year, prev.month - 1);
            if (direction === 'next') {
              newDate.setMonth(newDate.getMonth() + 1);
            } else {
              newDate.setMonth(newDate.getMonth() - 1);
            }
            return {
              month: newDate.getMonth() + 1,
              year: newDate.getFullYear()
            };
          });
        }}
      />

      <div className="space-y-8">
        {/* Métricas Principais */}
        <div className="grid grid-cols-4 gap-6">
          <MetricCard
            title="Receita Total"
            value={formatCurrency(metrics.revenue)}
            trend={{
              value: metrics.revenueGrowth,
              isPositive: metrics.revenueGrowth >= 0
            }}
            icon={DollarSign}
            iconColor="text-green-500"
            description="Total faturado no período"
            isLoading={isLoading}
          />

          <MetricCard
            title="Lucro"
            value={formatCurrency(metrics.profit)}
            trend={{
              value: metrics.profitGrowth,
              isPositive: metrics.profitGrowth >= 0
            }}
            icon={TrendingUp}
            iconColor="text-blue-500"
            description="Lucro líquido no período"
            isLoading={isLoading}
          />

          <MetricCard
            title="Margem de Lucro"
            value={`${metrics.profitMargin.toFixed(1)}%`}
            icon={Target}
            iconColor="text-purple-500"
            description="Percentual de lucro sobre receita"
            isLoading={isLoading}
          />

          <MetricCard
            title="Total de Lojas"
            value={metrics.totalStores}
            icon={Users}
            iconColor="text-amber-500"
            description="Lojas ativas no período"
            isLoading={isLoading}
          />
        </div>

        {/* Navegação entre Visões */}
        <div className="flex items-center gap-4 p-1 bg-[#111]/80 rounded-lg w-fit">
          <button
            onClick={() => setActiveSection('overview')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'overview'
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Visão Geral
          </button>
          <button
            onClick={() => setActiveSection('details')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'details'
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Detalhamento
          </button>
          <button
            onClick={() => setActiveSection('settlement')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'settlement'
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Acerto
          </button>
        </div>

        {/* Conteúdo Principal */}
        {activeSection === 'overview' ? (
          <FinancialAnalytics stores={filteredStores} />
        ) : activeSection === 'details' ? (
          <div className="grid grid-cols-3 gap-6">
            <PlanTable
              title="Plano Branding"
              stores={brandingStores}
              showValues={showValues}
              accentColor="bg-amber-500"
            />
            <PlanTable
              title="Plano Premium"
              stores={premiumStores}
              showValues={showValues}
              accentColor="bg-blue-500"
            />
            <PlanTable
              title="Plano Start"
              stores={startStores}
              showValues={showValues}
              accentColor="bg-green-500"
            />
          </div>
        ) : (
          <SettlementView 
            stores={stores}
            selectedDate={selectedDate}
            onNavigateMonth={handleNavigateMonth}
            formatMonthYear={formatMonthYear}
          />
        )}
      </div>
    </div>
  );
};
